// ResizeAdapter — Default implementation using ResizeObserver
// Injected into VNInvestCharts to avoid direct window/document access
export const createDefaultResizeAdapter = () => {
    const observers = new Map();
    return {
        observe: (element, callback) => {
            // Handle missing ResizeObserver gracefully (SSR, older browsers)
            if (typeof ResizeObserver === 'undefined') {
                return () => { }; // No-op cleanup
            }
            // Create or reuse ResizeObserver for this element
            let observer = observers.get(element);
            if (!observer) {
                observer = new ResizeObserver((entries) => {
                    // Only fire for the first entry (the observed element)
                    if (entries.length > 0) {
                        callback(entries[0]);
                    }
                });
                observer.observe(element);
                observers.set(element, observer);
            }
            // Return cleanup function
            return () => {
                observer?.unobserve(element);
                // Clean up observer if no more elements
                if (observer) {
                    // Check if observer has any other targets
                    // Note: ResizeObserver doesn't expose target count, so we track manually
                    // In practice, we'd need a ref count per element
                }
            };
        },
    };
};
// Singleton instance for convenience
export const defaultResizeAdapter = createDefaultResizeAdapter();
export const createRefCountedResizeAdapter = () => {
    const observers = new Map();
    return {
        observe: (element, callback) => {
            // Handle missing ResizeObserver gracefully (SSR, older browsers)
            if (typeof ResizeObserver === 'undefined') {
                return () => { }; // No-op cleanup
            }
            let entry = observers.get(element);
            if (!entry) {
                const observer = new ResizeObserver((entries) => {
                    if (entries.length > 0) {
                        entry?.callbacks.forEach(cb => cb(entries[0]));
                    }
                });
                observer.observe(element);
                entry = { observer, callbacks: new Set(), refCount: 0 };
                observers.set(element, entry);
            }
            entry.callbacks.add(callback);
            entry.refCount++;
            return () => {
                entry.callbacks.delete(callback);
                entry.refCount--;
                if (entry.refCount === 0) {
                    entry.observer.unobserve(element);
                    entry.observer.disconnect();
                    observers.delete(element);
                }
            };
        },
    };
};
